import { TOOL_CHOOSER, GLOSSARY } from '@/mocks/technical';
import { SectionHeading } from './ui';

type Accent = 'emerald' | 'amber' | 'rose';

const ACCENT: Record<Accent, { chip: string; btn: string }> = {
  emerald: {
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    btn: 'bg-emerald-600 hover:bg-emerald-700',
  },
  amber: {
    chip: 'bg-amber-50 text-amber-700 ring-amber-100',
    btn: 'bg-amber-500 hover:bg-amber-600',
  },
  rose: {
    chip: 'bg-rose-50 text-rose-700 ring-rose-100',
    btn: 'bg-rose-600 hover:bg-rose-700',
  },
};

const TERMS = Object.keys(GLOSSARY).sort((a, b) => b.length - a.length);
const TERM_REGEX = new RegExp(`(${TERMS.map(escapeRegex).join('|')})`, 'g');

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function Term({ term, tip }: { term: string; tip: string }) {
  return (
    <span className="group relative inline-flex items-center gap-0.5">
      <span className="font-semibold text-slate-700">{term}</span>
      <span className="flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full bg-slate-200 text-slate-500 transition-colors group-hover:bg-slate-300">
        <i className="ri-question-line text-[9px]" />
      </span>
      <span className="pointer-events-none absolute bottom-full left-0 z-20 mb-1.5 hidden w-48 rounded-lg bg-slate-900 px-3 py-2 text-[11px] leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:block group-hover:opacity-100">
        {tip}
      </span>
    </span>
  );
}

function renderBenefit(text: string) {
  return text.split(TERM_REGEX).map((part, i) => {
    const tip = GLOSSARY[part];
    return tip ? <Term key={i} term={part} tip={tip} /> : <span key={i}>{part}</span>;
  });
}

export default function ToolChooser({ onSelect }: { onSelect: (anchor: string) => void }) {
  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <SectionHeading
        eyebrow="Hướng dẫn"
        title="Khi nào nên sử dụng công cụ nào?"
        subtitle="Đối chiếu nhu cầu phân tích của bạn với từng công cụ để chọn đúng mục cần dùng."
      />

      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
        {TOOL_CHOOSER.map((t, i) => {
          const a = ACCENT[t.accent as Accent];
          return (
            <div
              key={t.anchor}
              className="relative flex flex-col rounded-2xl border border-slate-200/80 bg-slate-50/50 p-6 transition-colors hover:border-slate-300"
            >
              <span className="pointer-events-none absolute right-5 top-4 font-display text-5xl font-bold leading-none text-slate-100">
                0{i + 1}
              </span>

              <span
                className={`mt-2 inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${a.chip}`}
              >
                {t.name}
              </span>

              <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Khi bạn cần</p>
              <h4 className="mt-1.5 text-sm font-bold leading-snug text-slate-900">{t.scenario}</h4>

              <p className="mt-2 text-xs leading-relaxed text-slate-500">{t.desc}</p>

              <ul className="mt-5 space-y-2.5 border-t border-slate-200/70 pt-4">
                {t.benefits.map((b) => (
                  <li key={b} className="text-xs leading-relaxed text-slate-600">
                    {renderBenefit(b)}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => onSelect(t.anchor)}
                className={`mt-6 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-xs font-semibold text-white transition-colors ${a.btn}`}
              >
                {t.cta}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}